// src/services/wechatStats.ts
// ============================================
// 微信公众号数据统计（v6.3.18）—— 营销中心公众号面板真实数据源
//
// 能力：
//   1. getArticleStats: datacube/getarticlesummary 图文群发每日数据（阅读/点赞/分享/收藏）
//   2. getUserStats:    datacube/getusersummary 用户增长（新增/取消/净增）
//   3. 未配置 AppID/AppSecret 或接口失败时返回 { available: false, reason: '...' }
//      —— 绝不返回模拟数据（直播面板要真实数据）
//
// 注意：数据接口（datacube）仅认证服务号/已认证订阅号可用；未开通权限时返回明确原因。
// ============================================

import { apiConfig } from './apiConfig';
import { WeChatTokenManager } from '../runtime/ContentRuntime/platforms/wechat/token';

export interface WeChatArticleStat {
  /** YYYY-MM-DD */
  date: string;
  views: number;
  likes: number;
  shares: number;
  favorites: number;
  /** 图文消息名（当日群发标题，可能多条） */
  title: string;
}

export interface WeChatUserStat {
  date: string;
  newUsers: number;
  cancelUsers: number;
  netGain: number;
  totalUsers: number;
}

export interface WeChatStatsResult<T> {
  available: boolean;
  data?: T;
  reason?: string; // 不可用原因（未配置/未开通数据接口/接口报错）
}

interface WeChatConfig {
  appId: string;
  appSecret: string;
  apiBase: string; // /wechat-api 代理根（datacube 是 /datacube/ 前缀）
}

function getConfig(): WeChatConfig | null {
  try {
    const config = apiConfig.get('wechat');
    if (!config || !config.enabled) return null;
    const appId = config.extra?.appId || '';
    const appSecret = config.extra?.appSecret || '';
    if (!appId || !appSecret) return null;
    return { appId, appSecret, apiBase: '/wechat-api' }; // datacube 走 /wechat-api/datacube/...
  } catch {
    return null;
  }
}

async function getToken(config: WeChatConfig): Promise<string | null> {
  try {
    return await WeChatTokenManager.getToken({
      appId: config.appId,
      appSecret: config.appSecret,
      proxyUrl: config.apiBase,
    });
  } catch {
    return null;
  }
}

/** 近 N 天日期列表（含今天），yyyy-mm-dd */
function lastNDays(n: number): string[] {
  const days: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days.reverse();
}

/**
 * 图文群发每日数据（datacube/getarticlesummary）。
 * 微信接口：POST /datacube/getarticlesummary?access_token=xxx
 *   body: { "begin_date": "2026-08-01", "end_date": "2026-08-07" }（最多 7 天）
 * 返回每条群发记录：int_page_read/int_page_read_user/like_num/share_num/collect_num...
 */
export async function getArticleStats(days = 7): Promise<WeChatStatsResult<WeChatArticleStat[]>> {
  const config = getConfig();
  if (!config) return { available: false, reason: '公众号未配置（Settings → 微信 → AppID/AppSecret）' };
  const token = await getToken(config);
  if (!token) return { available: false, reason: 'access_token 获取失败（检查 AppID/AppSecret）' };

  try {
    // 微信 datacube 每次最多 7 天；多段循环
    const results: WeChatArticleStat[] = [];
    const dates = lastNDays(days);
    for (let i = 0; i < dates.length; i += 7) {
      const chunk = dates.slice(i, i + 7);
      if (chunk.length === 0) break;
      const begin = chunk[0];
      const end = chunk[chunk.length - 1];
      const resp = await fetch(`${config.apiBase}/datacube/getarticlesummary?access_token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ begin_date: begin, end_date: end }),
      });
      const json = await resp.json();
      if (json.errcode && json.errcode !== 0) {
        // 常见：48001 = api 功能未授权（订阅号无 datacube 权限）
        if (json.errcode === 48001 || json.errcode === 87009 || json.errcode === 45009) {
          return { available: false, reason: `微信数据接口未开通（errcode=${json.errcode}）——订阅号默认无 datacube 权限，需认证服务号` };
        }
        return { available: false, reason: `微信数据接口报错（errcode=${json.errcode}）：${json.errmsg || ''}` };
      }
      const list = json.list || [];
      for (const item of list) {
        results.push({
          date: item.ref_date || '',
          views: item.int_page_read || 0,
          likes: item.like_num || 0,
          shares: item.share_num || 0,
          favorites: item.collect_num || 0,
          title: item.title || '',
        });
      }
    }
    return { available: true, data: results };
  } catch (err: any) {
    return { available: false, reason: `数据接口请求失败：${err?.message || String(err)}` };
  }
}

/**
 * 用户增长数据（datacube/getusersummary）。
 * POST /datacube/getusersummary?access_token=xxx  body: { begin_date, end_date }（最多 7 天）
 */
export async function getUserStats(days = 7): Promise<WeChatStatsResult<WeChatUserStat[]>> {
  const config = getConfig();
  if (!config) return { available: false, reason: '公众号未配置' };
  const token = await getToken(config);
  if (!token) return { available: false, reason: 'access_token 获取失败' };

  try {
    const results: WeChatUserStat[] = [];
    const dates = lastNDays(days);
    for (let i = 0; i < dates.length; i += 7) {
      const chunk = dates.slice(i, i + 7);
      if (chunk.length === 0) break;
      const resp = await fetch(`${config.apiBase}/datacube/getusersummary?access_token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ begin_date: chunk[0], end_date: chunk[chunk.length - 1] }),
      });
      const json = await resp.json();
      if (json.errcode && json.errcode !== 0) {
        if (json.errcode === 48001 || json.errcode === 87009) {
          return { available: false, reason: `微信数据接口未开通（errcode=${json.errcode}）——订阅号默认无 datacube 权限` };
        }
        return { available: false, reason: `用户数据接口报错（errcode=${json.errcode}）：${json.errmsg || ''}` };
      }
      const list = json.list || [];
      for (const item of list) {
        results.push({
          date: item.ref_date || '',
          newUsers: item.new_user || 0,
          cancelUsers: item.cancel_user || 0,
          netGain: (item.new_user || 0) - (item.cancel_user || 0),
          totalUsers: item.cumulate_user || 0,
        });
      }
    }
    return { available: true, data: results };
  } catch (err: any) {
    return { available: false, reason: `用户数据接口请求失败：${err?.message || String(err)}` };
  }
}
